import { Box, Heading } from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getAllBanks } from '@/api/settings.api'
import type { Order, OrderStatus } from '@/common/order.common'
import type { Bank } from '@/common/settings.common'
import { UNION_BANK_ID } from '@/common/unionBank.common'
import { resolveOrderCustomerName } from '@/helper/order.helper'
import { usePlanningYears } from '@/hooks/usePlanningYears'
import { loadAllOrders } from './loadAllOrders'
import { getOrderYearOptions } from './orderOverview.helpers'
import {
  UnionOrderOverviewFilters,
  type OrderOverviewScope,
  type OrderOverviewSortBy,
  type OrderOverviewSortDir,
} from './UnionOrderOverviewFilters'
import { UnionOrderOverviewTable } from './UnionOrderOverviewTable'

const ALL_STATUSES: OrderStatus[] = [
  'SUBMITTED',
  'APPROVED',
  'REJECTED',
  'CORRECTED',
  'COMPLETED',
  'DELETED',
]

// The scope tabs are presets for the status filter: clicking one checks exactly
// its statuses in the Filter popover. "Offen" is the actionable worklist (Open +
// In Correction), "Archiv" the finished outcomes, "Alle" everything (see SIB-811).
const SCOPE_PRESETS: Record<OrderOverviewScope, OrderStatus[]> = {
  OPEN: ['SUBMITTED', 'CORRECTED'],
  ARCHIVE: ['APPROVED', 'REJECTED', 'COMPLETED'],
  ALL: ALL_STATUSES,
}

function sameStatusSet(a: OrderStatus[], b: OrderStatus[]): boolean {
  return a.length === b.length && a.every(s => b.includes(s))
}

// Which scope tab (if any) the current status selection corresponds to, so the
// tab highlights stay in sync when the user edits the checkboxes directly.
function matchScope(statuses: OrderStatus[]): OrderOverviewScope | null {
  return (
    (Object.keys(SCOPE_PRESETS) as OrderOverviewScope[]).find(scope =>
      sameStatusSet(SCOPE_PRESETS[scope], statuses)
    ) ?? null
  )
}

function sibNameParts(o: Order): string[] {
  return (o.username ?? o.userName ?? o.userId ?? '').trim().split(/\s+/).filter(Boolean)
}

function sibFirstName(o: Order): string {
  return sibNameParts(o)[0] ?? ''
}

function sibLastName(o: Order): string {
  const parts = sibNameParts(o)
  return parts.length > 1 ? parts[parts.length - 1] : (parts[0] ?? '')
}

function sortOrders(
  rows: Order[],
  sortBy: OrderOverviewSortBy,
  sortDir: OrderOverviewSortDir
): Order[] {
  const dir = sortDir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    switch (sortBy) {
      // The SIB name arrives as a single display string; split on whitespace so
      // the first token is the Vorname and the last is the Nachname.
      case 'sibLastName':
        return sibLastName(a).localeCompare(sibLastName(b), 'de') * dir
      case 'sibFirstName':
        return sibFirstName(a).localeCompare(sibFirstName(b), 'de') * dir
      case 'status':
        return (a.status ?? '').localeCompare(b.status ?? '', 'de') * dir
      case 'date':
      default:
        return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir
    }
  })
}

type UnionOrderOverviewSectionProps = {
  onSelectOrder?: (order: Order) => void
}

export function UnionOrderOverviewSection({ onSelectOrder }: UnionOrderOverviewSectionProps) {
  const [allOrders, setAllOrders] = useState<Order[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(false)

  const [searchTerm, setSearchTerm] = useState('')
  const [activeStatuses, setActiveStatuses] = useState<OrderStatus[]>(SCOPE_PRESETS.OPEN)
  const [year, setYear] = useState<number | null>(null)
  // Submission-date month, 1-12, null = all months (SIB-911).
  const [month, setMonth] = useState<number | null>(null)
  // Order year (SIB-1073): the planning/service year the SIB ordered for, i.e. the year of
  // startTime - not the submission date above. null = all order years, the view before SIB-1073.
  const [orderYear, setOrderYear] = useState<number | null>(null)
  const planningYears = usePlanningYears()
  const orderYearOptions = getOrderYearOptions(planningYears.currentYear, planningYears.openYears)

  // The highlighted scope tab is derived from the status selection so the two
  // never drift; editing the checkboxes into a preset re-lights that tab.
  const scope = matchScope(activeStatuses)
  const [sortBy, setSortBy] = useState<OrderOverviewSortBy>('date')
  const [sortDir, setSortDir] = useState<OrderOverviewSortDir>('desc')

  // The server filters by order year, over every page, so the other filters below always work
  // on the complete result. Only the latest request may land: switching years quickly must not
  // let an older, slower answer replace the current selection.
  const latestRequest = useRef(0)
  const load = useCallback(async () => {
    const request = ++latestRequest.current
    setIsLoading(true)
    setError(false)
    try {
      const orders = await loadAllOrders(orderYear != null ? { year: orderYear } : {})
      if (request === latestRequest.current) setAllOrders(orders)
    } catch {
      if (request === latestRequest.current) setError(true)
    } finally {
      if (request === latestRequest.current) setIsLoading(false)
    }
  }, [orderYear])

  useEffect(() => {
    load()
  }, [load])

  // The Union's child banks (the possible service recipients), needed to
  // resolve which bank each order is for. On new orders order.bankName holds
  // the Union parent bank, not the recipient — the recipient is the associated
  // entity referenced by customerId (see resolveOrderCustomerName).
  const [childBanks, setChildBanks] = useState<Bank[]>([])
  useEffect(() => {
    let cancelled = false
    getAllBanks({ parentId: UNION_BANK_ID })
      .then(list => {
        if (!cancelled) setChildBanks(list)
      })
      .catch(() => {
        // Search degrades to SIB name + legacy bankName matching.
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Years present in the data, newest first — drives the "Jahr" dropdown.
  // Based on the submission date (createdAt), like the month/year filter itself.
  // The selected year stays listed even when an order-year filter leaves no
  // order submitted in it, so the dropdown never shows a choice it isn't applying.
  const availableYears = useMemo(() => {
    const years = new Set<number>()
    for (const o of allOrders) {
      const y = new Date(o.createdAt).getFullYear()
      if (!Number.isNaN(y)) years.add(y)
    }
    if (year != null) years.add(year)
    return [...years].sort((a, b) => b - a)
  }, [allOrders, year])

  // Default the year filter once, to the current year if the data has it,
  // otherwise the most recent year available. A later user clear (year=null)
  // sticks because we only seed this once.
  const yearInitialized = useRef(false)
  useEffect(() => {
    if (yearInitialized.current || availableYears.length === 0) return
    const currentYear = new Date().getFullYear()
    setYear(availableYears.includes(currentYear) ? currentYear : availableYears[0])
    yearInitialized.current = true
  }, [availableYears])

  // Picking an order year shows every order for that year: the preselected
  // order-date year would otherwise hide orders placed ahead, e.g. 2027 orders
  // placed in late 2026. The user can still narrow by order date afterwards.
  const changeOrderYear = (next: number | null) => {
    setOrderYear(next)
    if (next != null) {
      setYear(null)
      yearInitialized.current = true
    }
  }

  const rows = useMemo(() => {
    let result = allOrders

    // Month/year filter on the submission date (createdAt), independent of
    // scope/status. Each part applies on its own, so "März" without a year
    // matches March of every year. Until SIB-911 the year went by startTime
    // (ordered service period); it now matches the "Bestelldatum" sort.
    if (year != null) {
      result = result.filter(o => new Date(o.createdAt).getFullYear() === year)
    }
    if (month != null) {
      result = result.filter(o => new Date(o.createdAt).getMonth() + 1 === month)
    }

    // Status filter: show exactly the checked statuses. The scope tabs seed this
    // selection (see SCOPE_PRESETS). An empty selection means no status is chosen,
    // so nothing matches.
    result = result.filter(o => activeStatuses.includes(o.status as OrderStatus))

    // Search matches the SIB name, the order's service recipient — the same
    // bank name the review screen displays (resolveOrderCustomerName) — and
    // the recipient's BLZ. Matching the raw order.bankName here was the
    // SIB-1011 bug: on new orders that field holds the Union parent bank, so
    // recipient searches found nothing, and where it held a legacy bank name
    // it matched orders whose displayed bank was a different one. BLZ is
    // compared as a string (leading zeros kept), partial like the SIB-632
    // recipient comboboxes.
    const q = searchTerm.trim().toLowerCase()
    if (q) {
      result = result.filter(o => {
        const sib = (o.username ?? o.userName ?? o.userId ?? '').toLowerCase()
        if (sib.includes(q)) return true

        const recipientName = resolveOrderCustomerName(o, childBanks).toLowerCase()
        if (recipientName.includes(q)) return true

        const customerId = Number(o.customerId ?? 0)
        const recipient =
          customerId > 0 ? childBanks.find(b => Number(b.id) === customerId) : undefined
        return (recipient?.blzNum ?? '').toLowerCase().includes(q)
      })
    }

    return sortOrders(result, sortBy, sortDir)
  }, [allOrders, activeStatuses, year, month, searchTerm, sortBy, sortDir, childBanks])

  // "Narrowed beyond the default scope?" — drives the empty-state wording. A plain
  // scope preset isn't treated as a filter, but search, year or a custom status
  // selection are.
  const isFiltered =
    searchTerm.trim().length > 0 ||
    orderYear != null ||
    year != null ||
    month != null ||
    (scope === null && activeStatuses.length > 0)

  return (
    <Box w="full">
      <Heading size="lg" fontWeight="normal" mb={4}>
        Bestellübersicht
      </Heading>

      <Box mb={4}>
        <UnionOrderOverviewFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          scope={scope}
          onScopeChange={s => setActiveStatuses(SCOPE_PRESETS[s])}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={(by, dir) => {
            setSortBy(by)
            setSortDir(dir)
          }}
          orderYear={orderYear}
          orderYearOptions={orderYearOptions}
          onOrderYearChange={changeOrderYear}
          year={year}
          availableYears={availableYears}
          onYearChange={setYear}
          month={month}
          onMonthChange={setMonth}
          activeStatuses={activeStatuses}
          onApplyStatuses={setActiveStatuses}
        />
      </Box>

      <UnionOrderOverviewTable
        rows={rows}
        isLoading={isLoading}
        error={error}
        onRetry={load}
        isFiltered={isFiltered}
        onSelectOrder={onSelectOrder}
      />
    </Box>
  )
}
